// Helper functions for note data transformations and utilities

/**
 * Transform API note data to include enhanced UI fields
 */
export const transformNoteData = (note, currentUserId) => {
  // Add author information
  const author = {
    id: note.userId || note.userEmail,
    name: note.userDisplayName || 'Unknown User',
    email: note.userId || note.userEmail,
    initials: getInitials(note.userDisplayName)
  };

  // Determine action type based on note content
  let action = 'added a note';
  if (note.tags?.some(tag => tag.type === 'analysis')) {
    action = 'added analysis';
  } else if (note.tags?.some(tag => tag.type === 'update')) {
    action = 'shared an update';
  } else if (note.replies?.length > 0) {
    action = 'started a discussion';
  }

  // Ensure tags have proper structure (preserve color information)
  const tags = note.tags || [];
  const formattedTags = tags.map(tag => {
    if (typeof tag === 'string') {
      return {
        id: tag.toLowerCase(),
        label: tag,
        type: tag.toLowerCase(),
        color: '#f97316' // Default orange color for string tags
      };
    }
    // Preserve existing tag structure including color
    return {
      ...tag,
      color: tag.color || '#f97316' // Ensure color exists
    };
  });

  return {
    ...note,
    author,
    action,
    tags: formattedTags,
    priority: note.priority || 'low',
    isOwn: note.userId === currentUserId
  };
};

/**
 * Get initials from a name
 */
export const getInitials = (name) => {
  if (!name) return '??';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Get author color class based on name or id
 */
export const getAuthorColorClass = (author) => {
  const colors = ['primary', 'sarah', 'mike', 'jen', 'alex', 'chris', 'dana'];
  if (!author) return colors[0];
  
  const identifier = author.name || author.id || '';
  const hash = identifier.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

/**
 * Format relative time from date string
 */
export const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  
  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
};

/**
 * Extract mentions from text
 */
export const extractMentions = (text) => {
  const mentionRegex = /@([^@\s]+)/g;
  const mentions = [];
  let match;
  
  while ((match = mentionRegex.exec(text)) !== null) {
    mentions.push({
      name: match[1],
      position: match.index
    });
  }
  
  return mentions;
};

/**
 * Parse mentions in text and return React-friendly array
 */
export const parseMentions = (text) => {
  if (!text) return [];
  
  const mentionRegex = /@(\S+)/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  
  while ((match = mentionRegex.exec(text)) !== null) {
    // Add text before mention
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex, match.index)
      });
    }
    
    // Add mention
    parts.push({
      type: 'mention',
      content: match[1]
    });
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.substring(lastIndex)
    });
  }
  
  return parts;
};

/**
 * Get priority level configuration
 */
export const getPriorityConfig = (priority) => {
  const configs = {
    low: {
      color: '#10b981',
      label: 'Low',
      className: 'priority-low'
    },
    medium: {
      color: '#f59e0b',
      label: 'Medium',
      className: 'priority-medium'
    },
    high: {
      color: '#ef4444',
      label: 'High',
      className: 'priority-high'
    }
  };
  
  return configs[priority] || configs.low;
};

/**
 * Available tag configurations
 */
export const availableTags = [
  { id: 'forecast', label: 'Forecast', type: 'forecast', color: '#1e40af' },
  { id: 'revenue', label: 'Revenue', type: 'revenue', color: '#166534' },
  { id: 'urgent', label: 'Urgent', type: 'urgent', color: '#991b1b' },
  { id: 'operations', label: 'Operations', type: 'operations', color: '#92400e' },
  { id: 'analysis', label: 'Analysis', type: 'analysis', color: '#3730a3' },
  { id: 'meeting', label: 'Meeting', type: 'meeting', color: '#6b21a8' },
  { id: 'update', label: 'Update', type: 'update', color: 'var(--color-info-ink)' }
];

/**
 * Filter notes by search term
 */
export const filterNotesBySearch = (notes, searchTerm) => {
  if (!searchTerm) return notes;
  
  const term = searchTerm.toLowerCase();
  return notes.filter(note => {
    return (
      note.noteText?.toLowerCase().includes(term) ||
      note.title?.toLowerCase().includes(term) ||
      note.userDisplayName?.toLowerCase().includes(term) ||
      note.tags?.some(tag => tag.label?.toLowerCase().includes(term)) ||
      note.replies?.some(reply => reply.replyText?.toLowerCase().includes(term))
    );
  });
};

/**
 * Sort notes by various criteria
 */
export const sortNotes = (notes, sortBy = 'recent') => {
  const sorted = [...notes];
  
  switch (sortBy) {
    case 'recent':
      return sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    
    case 'priority':
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return sorted.sort((a, b) => {
        const aPriority = priorityOrder[a.priority || 'low'];
        const bPriority = priorityOrder[b.priority || 'low'];
        if (aPriority !== bPriority) return aPriority - bPriority;
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
    
    case 'replies':
      return sorted.sort((a, b) => {
        const aReplies = a.replies?.length || 0;
        const bReplies = b.replies?.length || 0;
        if (aReplies !== bReplies) return bReplies - aReplies;
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
    
    default:
      return sorted;
  }
};

/**
 * Group notes by context
 */
export const groupNotesByContext = (notes, currentDataContext) => {
  const contextual = [];
  const other = [];
  
  notes.forEach(note => {
    if (!currentDataContext || !note.dataContext) {
      if (!currentDataContext) {
        other.push(note);
      } else {
        contextual.push(note);
      }
    } else {
      const isContextual = JSON.stringify(note.dataContext.dataPointIdentity) === 
                          JSON.stringify(currentDataContext.dataPointIdentity);
      if (isContextual) {
        contextual.push(note);
      } else {
        other.push(note);
      }
    }
  });
  
  return { contextual, other };
};

export default {
  transformNoteData,
  getInitials,
  getAuthorColorClass,
  formatRelativeTime,
  extractMentions,
  parseMentions,
  getPriorityConfig,
  availableTags,
  filterNotesBySearch,
  sortNotes,
  groupNotesByContext
};